import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Crown, PlaneTakeoff, ShieldAlert, Users, Bot } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    id: "circashift_pro_monthly",
    label: "Monthly",
    price: "$3.99",
    per: "/mo",
    tagline: "Cancel anytime",
  },
  {
    id: "circashift_pro_yearly",
    label: "Yearly",
    price: "$39",
    per: "/yr",
    tagline: "Save 18% · $3.25/mo",
    badge: "Best value",
  },
];

const PRO_FEATURES = [
  { icon: Bot, text: "Unlimited AI Advisor (Claude Sonnet 5)" },
  { icon: PlaneTakeoff, text: "Jet Lag Mode — multi-leg travel planner" },
  { icon: ShieldAlert, text: "Safety Alertness Score for every shift" },
  { icon: Users, text: "Team Leaderboard & persona filters" },
  { icon: Crown, text: "All badges + weekly rota presets" },
];

export default function PricingDialog({ open, onOpenChange }) {
  const [selected, setSelected] = useState("circashift_pro_yearly");
  const [busy, setBusy] = useState(false);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="pill mx-auto mb-3 bg-indigo-950/60 border-indigo-500/40 text-indigo-200 w-fit">
            <Sparkles className="w-3.5 h-3.5" /> 7-day free trial · cancel anytime
          </div>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-extrabold text-center">
            Upgrade to CircaShift Pro
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm text-center">
            Unlock the tools built for the toughest schedules.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {PLANS.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              data-testid={`pricing-plan-${p.id}`}
              className={`text-left rounded-2xl border p-4 transition relative ${
                selected === p.id
                  ? "border-indigo-400 bg-indigo-500/10 ring-1 ring-indigo-400/40"
                  : "border-white/10 bg-slate-900/50 hover:border-indigo-400/40"
              }`}
            >
              {p.badge && (
                <span className="absolute -top-2 right-3 pill text-[9px] bg-amber-950/70 border-amber-500/40 text-amber-200">
                  <Crown className="w-3 h-3" /> {p.badge}
                </span>
              )}
              <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-400 mb-2">{p.label}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl font-extrabold">{p.price}</span>
                <span className="text-slate-500 text-sm">{p.per}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">{p.tagline}</div>
            </button>
          ))}
        </div>

        <ul className="mt-4 space-y-2">
          {PRO_FEATURES.map((f, i) => (
            <li key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center shrink-0">
                <f.icon className="w-4 h-4 text-indigo-300" />
              </div>
              <span className="text-sm text-slate-200">{f.text}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={upgrade}
          disabled={busy}
          data-testid="btn-start-trial"
          className="w-full mt-4 h-11 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
        >
          <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Opening checkout…" : "Start free 7-day trial"}
        </Button>
        <p className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500 text-center mt-2">
          No charge for 7 days · Cancel from any device
        </p>
      </DialogContent>
    </Dialog>
  );
}
